/**
 * PERIOD CLOSING SERVICE
 *
 * Once-per-period closings (week / month / quarter / year) built from daily closings.
 * Month / quarter / year start in reviewStatus=pending_review and block sync until approved.
 */

import { getCurrentBusinessId } from '../utils/sharedCore';
import { kvGet, kvSet, kvSetPersisted } from '../infrastructure/storage/kvAccess';
import dailyClosingService, { toLocalDateString } from './dailyClosingService';
import balanceSheetService from './balanceSheetService';
import {
  PERIOD_TYPES,
  REVIEW_BLOCKING_PERIOD_TYPES,
  dateKeyInRange,
  monthsInQuarter,
  quartersInYear,
  getDuePeriods
} from '../domain/operations/periodKeys';

export const PERIOD_CLOSING_UPDATED_EVENT = 'aman:periodClosingUpdated';

function dispatchPeriodClosingUpdated(businessId, detail = {}) {
  if (typeof window === 'undefined' || !businessId) return;
  window.dispatchEvent(
    new CustomEvent(PERIOD_CLOSING_UPDATED_EVENT, {
      detail: { businessId, ...detail }
    })
  );
}

function emptySummary() {
  return {
    revenue: 0,
    purchases: 0,
    expenses: 0,
    payments: 0,
    net: 0,
    salesCount: 0,
    purchasesCount: 0,
    expensesCount: 0,
    paymentsCount: 0,
    dailyClosingsCount: 0
  };
}

function sumDailySummaries(dailyClosings) {
  const summary = emptySummary();
  for (const closing of dailyClosings) {
    const s = closing?.summary || {};
    summary.revenue += Number(s.revenue || 0);
    summary.purchases += Number(s.purchases || s.purchasesTotal || 0);
    summary.expenses += Number(s.expenses || s.expensesTotal || s.expensesRegister || 0);
    summary.payments += Number(s.payments || s.paymentsTotal || 0);
    summary.net += Number(s.net ?? s.netCash ?? 0);
    summary.salesCount += Number(s.salesCount || 0);
    summary.purchasesCount += Number(s.purchasesCount || 0);
    summary.expensesCount += Number(s.expensesCount || 0);
    summary.paymentsCount += Number(s.paymentsCount || 0);
  }
  summary.dailyClosingsCount = dailyClosings.length;
  return summary;
}

function resolvePeriodBalanceSheet(dailyClosings, period, extras = {}) {
  if (!dailyClosings.length) return null;
  const sorted = dailyClosings
    .slice()
    .sort((a, b) => String(a.date).localeCompare(String(b.date)));
  const last = sorted[sorted.length - 1];
  let balanceSheet = last?.balanceSheet || null;
  const capitalMissing = !(Number(balanceSheet?.equity?.capital) > 0);
  if ((!balanceSheet || capitalMissing) && last?.closingSummary) {
    balanceSheet = balanceSheetService.reconstructBalanceSheetFromClosingSummary(last, extras);
  }
  if (!balanceSheet) {
    balanceSheet = balanceSheetService.calculateYearlyBalanceSheetFromClosings(
      sorted,
      period.start.getFullYear(),
      extras
    );
  }
  return {
    ...balanceSheet,
    period: {
      type: period.type,
      periodKey: period.periodKey,
      startDate: period.startDate,
      endDate: period.endDate,
      dailyClosingsCount: sorted.length
    }
  };
}

class PeriodClosingService {
  constructor() {
    this.storageKeyPrefix = 'periodClosing_';
  }

  getStorageKey(businessId) {
    return `${this.storageKeyPrefix}${businessId || getCurrentBusinessId()}`;
  }

  getAllClosings(businessId = null) {
    try {
      const stored = kvGet(this.getStorageKey(businessId));
      if (stored == null) return [];
      if (Array.isArray(stored)) return stored;
      if (typeof stored === 'string') {
        try {
          const parsed = JSON.parse(stored);
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      }
      return [];
    } catch (error) {
      console.error('[PERIOD CLOSING] load failed:', error);
      return [];
    }
  }

  getClosing(type, periodKey, businessId = null) {
    return (
      this.getAllClosings(businessId).find(
        (c) => c?.type === type && c?.periodKey === periodKey && c?.status === 'closed'
      ) || null
    );
  }

  isPeriodClosed(type, periodKey, businessId = null) {
    return Boolean(this.getClosing(type, periodKey, businessId));
  }

  async saveClosing(closing, businessId = null, { persisted = true } = {}) {
    const bid = businessId || getCurrentBusinessId();
    const storageKey = this.getStorageKey(bid);
    const existing = this.getAllClosings(bid).filter(
      (c) => !(c?.type === closing.type && c?.periodKey === closing.periodKey)
    );
    existing.push(closing);
    existing.sort((a, b) => String(b.endDate || '').localeCompare(String(a.endDate || '')));
    if (persisted) {
      await kvSetPersisted(storageKey, existing);
    } else {
      kvSet(storageKey, existing);
    }
    return true;
  }

  getDailyClosingsInRange(startDate, endDate, businessId = null) {
    return dailyClosingService
      .getAllClosings(businessId)
      .filter((c) => c?.status === 'closed')
      .filter((c) => {
        const key = dailyClosingService.normalizeClosingDateKey(c.date);
        return dateKeyInRange(key, startDate, endDate);
      })
      .sort((a, b) =>
        String(dailyClosingService.normalizeClosingDateKey(a.date)).localeCompare(
          String(dailyClosingService.normalizeClosingDateKey(b.date))
        )
      );
  }

  getChildPeriodClosings(type, periodKeys, businessId = null) {
    return periodKeys
      .map((periodKey) => this.getClosing(type, periodKey, businessId))
      .filter(Boolean);
  }

  /**
   * Build & persist a period closing if not already closed.
   * Skips empty periods (no daily closings and no child period closings).
   */
  async performPeriodClosing(period, closedBy = 'system', businessId = null, options = {}) {
    const bid = businessId || getCurrentBusinessId();
    if (!bid || !period?.type || !period?.periodKey) {
      return { success: false, skipped: true, reason: 'invalid_period' };
    }

    if (this.isPeriodClosed(period.type, period.periodKey, bid)) {
      return { success: false, skipped: true, reason: 'already_closed', periodKey: period.periodKey };
    }

    const dailyClosings = this.getDailyClosingsInRange(period.startDate, period.endDate, bid);
    let sourcePeriodKeys = [];
    let childClosings = [];
    let expectChildren = 0;

    if (period.type === PERIOD_TYPES.QUARTER) {
      const [y, qPart] = period.periodKey.split('-Q');
      sourcePeriodKeys = monthsInQuarter(Number(y), Number(qPart));
      childClosings = this.getChildPeriodClosings(PERIOD_TYPES.MONTH, sourcePeriodKeys, bid);
      expectChildren = 3;
    } else if (period.type === PERIOD_TYPES.YEAR) {
      sourcePeriodKeys = quartersInYear(Number(period.periodKey));
      childClosings = this.getChildPeriodClosings(PERIOD_TYPES.QUARTER, sourcePeriodKeys, bid);
      expectChildren = 4;
      if (childClosings.length < 4) {
        sourcePeriodKeys = Array.from({ length: 12 }, (_, m) =>
          `${period.periodKey}-${String(m + 1).padStart(2, '0')}`
        );
        childClosings = this.getChildPeriodClosings(PERIOD_TYPES.MONTH, sourcePeriodKeys, bid);
        expectChildren = 12;
      }
    }

    if (dailyClosings.length === 0 && childClosings.length === 0) {
      return { success: false, skipped: true, reason: 'no_activity', periodKey: period.periodKey };
    }

    let summary;
    let balanceSheet;

    // Prefer complete child periods (3 months / 4 quarters / 12 months); else daily closings.
    const useChildren = expectChildren > 0 && childClosings.length === expectChildren;
    let bilanExtras = {};
    try {
      const businessInfoService = (await import('./businessInfoService')).default;
      const info = await businessInfoService.getBusinessInfo(bid);
      if (info) {
        bilanExtras = {
          openingCapital: info.openingCapital || null,
          fixedAssets: info.fixedAssets || []
        };
      }
    } catch {
      /* optional — closings may lack capital otherwise */
    }

    if (useChildren) {
      summary = emptySummary();
      for (const child of childClosings) {
        const s = child.summary || {};
        summary.revenue += Number(s.revenue || 0);
        summary.purchases += Number(s.purchases || 0);
        summary.expenses += Number(s.expenses || 0);
        summary.payments += Number(s.payments || 0);
        summary.net += Number(s.net || 0);
        summary.salesCount += Number(s.salesCount || 0);
        summary.purchasesCount += Number(s.purchasesCount || 0);
        summary.expensesCount += Number(s.expensesCount || 0);
        summary.paymentsCount += Number(s.paymentsCount || 0);
        summary.dailyClosingsCount += Number(s.dailyClosingsCount || 0);
      }
      const lastChild = childClosings
        .slice()
        .sort((a, b) => String(a.endDate).localeCompare(String(b.endDate)))
        .at(-1);
      balanceSheet = lastChild?.balanceSheet
        ? {
            ...lastChild.balanceSheet,
            period: {
              type: period.type,
              periodKey: period.periodKey,
              startDate: period.startDate,
              endDate: period.endDate,
              dailyClosingsCount: summary.dailyClosingsCount
            }
          }
        : resolvePeriodBalanceSheet(dailyClosings, period, bilanExtras);
    } else {
      summary = sumDailySummaries(dailyClosings);
      balanceSheet = resolvePeriodBalanceSheet(dailyClosings, period, bilanExtras);
    }

    const needsReview = REVIEW_BLOCKING_PERIOD_TYPES.includes(period.type);
    const closing = {
      id: `periodClosing_${period.type}_${period.periodKey}_${bid}`,
      type: period.type,
      periodKey: period.periodKey,
      startDate: period.startDate,
      endDate: period.endDate,
      closingTime: new Date().toISOString(),
      closedBy: closedBy || 'system',
      status: 'closed',
      reviewStatus: needsReview ? 'pending_review' : 'approved',
      reviewedAt: needsReview ? null : new Date().toISOString(),
      reviewedBy: needsReview ? null : closedBy || 'system',
      summary,
      balanceSheet,
      sourceDailyClosingDates: dailyClosings.map((c) =>
        dailyClosingService.normalizeClosingDateKey(c.date)
      ),
      sourcePeriodKeys,
      metadata: {
        createdAt: new Date().toISOString(),
        automatic: closedBy === 'system' || closedBy === 'automatic'
      }
    };

    await this.saveClosing(closing, bid, { persisted: options.persisted !== false });
    dispatchPeriodClosingUpdated(bid, {
      action: 'closed',
      type: period.type,
      periodKey: period.periodKey
    });

    return { success: true, closing, periodKey: period.periodKey };
  }

  /**
   * Catch-up: close every due period that is not yet closed (idempotent).
   */
  async checkAndPerformAutomaticPeriodClosings(closedBy = 'system', businessId = null, now = new Date()) {
    const bid = businessId || getCurrentBusinessId();
    if (!bid) {
      return { processed: [], errors: ['No business ID'], skipped: [] };
    }

    const due = getDuePeriods(now);
    const processed = [];
    const errors = [];
    const skipped = [];

    for (const period of due) {
      try {
        const result = await this.performPeriodClosing(period, closedBy, bid);
        if (result.success) {
          processed.push({
            type: period.type,
            periodKey: period.periodKey,
            reviewStatus: result.closing?.reviewStatus
          });
        } else if (result.skipped) {
          skipped.push({ type: period.type, periodKey: period.periodKey, reason: result.reason });
        } else {
          errors.push({ type: period.type, periodKey: period.periodKey, error: result.error });
        }
      } catch (error) {
        errors.push({
          type: period.type,
          periodKey: period.periodKey,
          error: error?.message || String(error)
        });
      }
    }

    return { processed, errors, skipped };
  }

  getPendingReviews(businessId = null) {
    return this.getAllClosings(businessId).filter(
      (c) =>
        c?.status === 'closed' &&
        REVIEW_BLOCKING_PERIOD_TYPES.includes(c.type) &&
        c.reviewStatus === 'pending_review'
    );
  }

  hasPendingReview(businessId = null) {
    return this.getPendingReviews(businessId).length > 0;
  }

  /**
   * Approve a period closing after manager research — unlocks sync when none remain pending.
   */
  async approvePeriodClosing(type, periodKey, reviewedBy = 'manager', businessId = null) {
    const bid = businessId || getCurrentBusinessId();
    const closing = this.getClosing(type, periodKey, bid);
    if (!closing) {
      return { success: false, error: 'not_found' };
    }
    if (closing.reviewStatus === 'approved') {
      return { success: true, closing, alreadyApproved: true };
    }

    const updated = {
      ...closing,
      reviewStatus: 'approved',
      reviewedAt: new Date().toISOString(),
      reviewedBy: reviewedBy || 'manager'
    };
    await this.saveClosing(updated, bid, { persisted: true });
    dispatchPeriodClosingUpdated(bid, {
      action: 'approved',
      type,
      periodKey
    });
    return { success: true, closing: updated };
  }

  async rejectPeriodClosing(type, periodKey, reviewedBy = 'manager', businessId = null, note = '') {
    const bid = businessId || getCurrentBusinessId();
    const closing = this.getClosing(type, periodKey, bid);
    if (!closing) {
      return { success: false, error: 'not_found' };
    }
    const updated = {
      ...closing,
      reviewStatus: 'rejected',
      reviewedAt: new Date().toISOString(),
      reviewedBy: reviewedBy || 'manager',
      reviewNote: note || ''
    };
    await this.saveClosing(updated, bid, { persisted: true });
    dispatchPeriodClosingUpdated(bid, {
      action: 'rejected',
      type,
      periodKey
    });
    return { success: true, closing: updated };
  }

  /** Soft delete (admin) — allows period to be closed again. */
  async deletePeriodClosing(type, periodKey, businessId = null) {
    const bid = businessId || getCurrentBusinessId();
    const storageKey = this.getStorageKey(bid);
    const closings = this.getAllClosings(bid);
    const filtered = closings.filter(
      (c) => !(c?.type === type && c?.periodKey === periodKey)
    );
    if (filtered.length === closings.length) return false;
    await kvSetPersisted(storageKey, filtered);
    dispatchPeriodClosingUpdated(bid, { action: 'deleted', type, periodKey });
    return true;
  }
}

const periodClosingService = new PeriodClosingService();
export default periodClosingService;
export { PERIOD_TYPES, toLocalDateString };
